import { Conditions, GroupCondition } from '@/api/enums';
import { equal, greater, group, handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import {
  CampaignStatusesEnum,
  SimulationPlanData,
  SimulationSchedulerItem,
  SimulationWithAssistancePlanData,
} from '@/client/campaigns';
import { SystemRoles } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { TranslationFunctionType } from '@/common/types';
import {
  AutoScheduleHeader,
  InfoCards,
  SimulationScheduler,
  WhitelistingBanner,
} from '@/components/campaigns/simulations';
import { AddSimulationsCard } from '@/components/campaigns/simulations/AddSimulationsCard';
import {
  ScheduleUsersWarningModal,
  WhitelistingModal,
} from '@/components/campaigns/simulations/modals';
import { PlanSimulationModal } from '@/components/campaigns/simulations/modals/PlanSimulationModal';
import { PlanWithAssistanceModal } from '@/components/campaigns/simulations/modals/PlanWithAssistanceModal';
import { SendTestEmailDialog } from '@/components/campaigns/simulations/modals/SendTestEmailDialog';
import { useNotifications } from '@/hooks/notifications.hook';
import { useAccount } from '@/hooks/query';
import {
  useAutoPhishingSummary,
  useCampaigns,
  useCreateAutoPhishingCampaign,
  useCreateAutoPhishingCampaigns,
  useDeleteCampaign,
  useLaunchAutoPhishingCampaigns,
  useUpdateCampaign,
} from '@/hooks/query/campaigns.hook';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useToast } from '@/hooks/useToast';
import { EmptyAutoPhishingPage } from '@/pages/campaigns/EmptyAutoPhishingPage';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DialogType } from '@/system-settings/enums';
import { FlexContainer } from '@/ui';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import {
  getPhishingSimulationStatusColors,
  isDemoRestrictedMode,
} from '@/utils/helpers';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { getTargetsAndExcludes } from '@/utils/targets';
import { AxiosError } from 'axios';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDescription = styled.p`
  color: var(--black-main);
  font-size: var(--small-font-size);
  margin: 0;
  line-height: 1.4;
`;

const DemoContainer = styled.div`
  min-height: 100vh;
`;

const SchedulerWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledScheduler = styled.div`
  .simulation-scheduler {
    border-radius: 0 0 var(--semi-medium-border-radius)
      var(--semi-medium-border-radius);
  }
`;

const Actions = styled(FlexContainer)`
  justify-content: flex-start;
  margin-top: var(--default-padding);
  gap: var(--small-padding);

  .info-message {
    i {
      color: var(--orange-main);
      font-size: var(--medium-font-size);
    }

    p {
      font-size: var(--xsmall-font-size);
      line-height: 150%;
      font-weight: 500;
      margin-left: var(--small-padding);
    }
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: var(--default-padding);
  color: var(--white-main);
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--default-padding);
    padding: 40px 60px;
  }

  .loading-text {
    font-size: 20px;
    font-weight: 500;
    text-align: center;
    margin: 0;
  }

  .loading-dots {
    display: inline-block;
    font-weight: 500;

    span {
      display: inline-block;
      animation: dotFade 1.4s ease-in-out infinite;
      opacity: 0.4;

      &:nth-child(1) {
        animation-delay: 0s;
      }

      &:nth-child(2) {
        animation-delay: 0.2s;
      }

      &:nth-child(3) {
        animation-delay: 0.4s;
      }
    }

    @keyframes dotFade {
      0%,
      60%,
      100% {
        opacity: 0.4;
      }
      30% {
        opacity: 1;
      }
    }
  }

  .loading-subtext {
    font-size: var(--small-font-size);
    font-weight: 400;
    opacity: 0.8;
    text-align: center;
    margin: 0;
  }

  svg circle {
    stroke: var(--green-color) !important;
  }
`;

const pathItems = (t: TranslationFunctionType, account: Account) => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('phishing.simulations.title'),
    url: RedirectPaths[RedirectPathsEnum.PHISHING_SIMULATIONS](),
    template: AppBreadCrumbTemplate,
  },
];

export const PhishingSimulationsPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { setDialogData } = useContext(DialogContext);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(currentAccount, currentUser);
  const { account: freshAccount } = useAccount({
    accountId: currentAccount?.id,
    enabled: !!currentAccount?.id && !currentAccount?.isSystem,
  });
  const isPhishingSenderWhitelisted =
    freshAccount?.isPhishingSenderWhitelisted ??
    currentAccount?.isPhishingSenderWhitelisted;
  const [campaingsMapped, setCampaingsMapped] = useState<
    SimulationSchedulerItem[]
  >([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isGeneratingSchedule, setIsGeneratingSchedule] = useState(false);
  const [isLaunchingSimulations, setIsLaunchingSimulations] = useState(false);
  const { canUseFeature } = useFeatureFlag();

  const currentDate = React.useMemo(
    () =>
      moment()
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toDate()
        .toISOString(),
    [],
  );
  const filters = useMemo(
    () => [
      {
        field: 'account',
        condition: Conditions.EQUAL,
        value: (currentAccount as Account)?.id,
      },
      {
        field: 'status',
        condition: Conditions.NOT_EQUAL,
        value: CampaignStatusesEnum.COMPLETED,
      },
      group(GroupCondition.OR, [
        greater('sendByDate', currentDate, true),
        greater('launchDate', currentDate, true),
        equal('status', CampaignStatusesEnum.DRAFT),
      ]),
    ],
    [currentAccount?.id, currentDate],
  );

  const {
    campaigns,
    refetch: refetchCampaigns,
    isLoading,
  } = useCampaigns({
    withTargets: 1,
    withDifficultyInMeta: 1,
    take: 200,
    skip: 0,
    enabled: !currentAccount?.isSystem,
    filters,
    sort: ['launchDate,asc'],
  });

  const {
    autoPhishingSummary,
    refetch: refetchAutoPhishingSummary,
    isLoading: isLoadingAutoPhishingSummary,
  } = useAutoPhishingSummary(currentAccount?.id, {
    enabled: !!currentAccount?.id,
    filters,
  });

  const {
    isLoading: isLoadingSimplifiedScreenCheck,
    campaigns: simplifiedScreenCheck,
    refetch: refetchSimplifiedScreenCheck,
  } = useCampaigns({
    take: 0,
    skip: 0,
    enabled: !currentAccount?.isSystem,
    filters: [
      equal('account', (currentAccount as Account)?.id),
      equal('isAutoPhishing', true),
    ],
  });

  useEffect(() => {
    if (!isLoading && initialLoad && !isLoadingAutoPhishingSummary) {
      setInitialLoad(false);
    }
  }, [isLoading, isLoadingAutoPhishingSummary, initialLoad]);

  useEffect(() => {
    if (campaigns) {
      const now = new Date();
      const mapped = campaigns.result.map((campaign) => {
        const startDate = campaign.launchDate
          ? new Date(campaign.launchDate)
          : null;
        const endDate =
          campaign.sendByDate || campaign.launchDate
            ? new Date(campaign.sendByDate || campaign.launchDate)
            : null;
        const isDraft = campaign.status === CampaignStatusesEnum.DRAFT;
        const isDateInvalid = isDraft && (!startDate || startDate < now);

        return {
          id: campaign.id,
          name: campaign.name || campaign.meta.cloneOfName,
          startDate: isDateInvalid ? null : startDate,
          endDate: isDateInvalid ? null : endDate,
          targetGroup: campaign.targets,
          targets: campaign.targets,
          color: getPhishingSimulationStatusColors(campaign.status),
          disabled: !isDraft,
          canDelete: campaign.status !== CampaignStatusesEnum.ACTIVE,
          campaignTemplateId: campaign.meta.cloneOf,
          difficulty: campaign.meta.cloneOfDifficulty,
          phishingSign: campaign.meta.phishingSign,
          status: campaign.status,
          targetUsersCount:
            campaign.summary?.total || campaign.targetUsersCount || 0,
          emailTemplateId: campaign.emailTemplate?.id,
          sendingProfileId: campaign.sendingProfile?.id,
          landingPageId: campaign.landingPage?.id,
        };
      });

      mapped.sort((a, b) => {
        const aDateMissing =
          a.status === CampaignStatusesEnum.DRAFT && !a.startDate;
        const bDateMissing =
          b.status === CampaignStatusesEnum.DRAFT && !b.startDate;
        if (aDateMissing && !bDateMissing) return 1;
        if (!aDateMissing && bDateMissing) return -1;
        return 0;
      });

      setCampaingsMapped(mapped);
    }
  }, [campaigns]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] =
    useState<SimulationSchedulerItem | null>(null);
  const [isWhitelistingModalOpen, setIsWhitelistingModalOpen] = useState(false);
  const [isPlanWithAssistanceModalOpen, setIsPlanWithAssistanceModalOpen] =
    useState(false);
  const [isUsersWarningModalOpen, setIsUsersWarningModalOpen] = useState(false);
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [testEmailSimulation, setTestEmailSimulation] =
    useState<SimulationSchedulerItem | null>(null);

  const debouncedUpdateRef = React.useRef(
    debounce(async (task: SimulationSchedulerItem) => {
      try {
        await updateCampaign.update({
          campaignId: task.id,
          updates: {
            launchDate: task.startDate ?? undefined,
            sendByDate: task.endDate ?? undefined,
          },
        });
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    }, 600),
  );

  const handleCampaignChange = (updatedCampaign: SimulationSchedulerItem) => {
    setCampaingsMapped((prev) =>
      prev.map((campaign) =>
        campaign.id === updatedCampaign.id ? updatedCampaign : campaign,
      ),
    );

    debouncedUpdateRef.current(updatedCampaign);
  };

  const handleCampaignEdit = (task: SimulationSchedulerItem) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const [deletingCampaignId, setDeletingCampaignId] = useState<string | null>(
    null,
  );

  const {
    lastMessage: lastMessageDeletion,
    setNotificationParam: setIdForDeletion,
    notificationParam: notificationForDeletion,
    isConnected: isConnectedForDeletion,
  } = useNotifications(client.campaigns.campaignsNotifyUrl);

  useEffect(() => {
    if (!notificationForDeletion) return;
    handleNotifyCampaignDelete(lastMessageDeletion);
  }, [JSON.stringify(lastMessageDeletion)]);

  const handleNotifyCampaignDelete = async (messages: any) => {
    if (
      !messages?.data?.event ||
      !['campaign.delete.finished', 'campaign.delete.error'].includes(
        messages?.data?.event,
      )
    )
      return;

    if (messages?.data?.event === 'campaign.delete.finished') {
      setDeletingCampaignId(null);
      await refetchAutoPhishingSummary();
      toast?.success(t('toast.success'), t('campaign.deleted'));
    }

    if (messages?.data?.event === 'campaign.delete.error') {
      setDeletingCampaignId(null);
      toast?.error(t('toast.error'), t('campaign.not.deleted'), 5000);
    }

    setIdForDeletion(undefined);
  };

  const deleteCampaign = useDeleteCampaign();
  const handleDeleteCampaign = async (campaignId: string) => {
    try {
      await deleteCampaign.delete(campaignId);
      await refetchSimplifiedScreenCheck();
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setDeletingCampaignId(null);
    }
  };

  const handleCampaignDelete = (campaignId: string) => {
    setDialogData({
      show: true,
      type: DialogType.CONFIRMATION,
      header: t('campaign.delete'),
      message: t('campaign.delete.confirm'),
      closeImmediatelyAfterAccept: true,
      onAccept: async () => {
        setIdForDeletion(campaignId);
        setDeletingCampaignId(campaignId);

        // Wait for a notify connection to establish
        await new Promise((resolve) => {
          const intervalId = setInterval(() => {
            if (isConnectedForDeletion.current) {
              clearInterval(intervalId);
              resolve(true);
            }
          }, 100);
        });
        try {
          if (isConnectedForDeletion.current) {
            await handleDeleteCampaign(campaignId);
            setCampaingsMapped((prev) =>
              prev.filter((campaign) => campaign.id !== campaignId),
            );
          }
        } catch (e) {
          handleAxiosError(e as Error | AxiosError, toast);
        }
      },
    });
  };

  const handleSingleSimulation = () => {
    setIsModalOpen(true);
  };

  const handlePlanWithAssistance = () => {
    setIsPlanWithAssistanceModalOpen(true);
  };

  const createAutoPhishing = useCreateAutoPhishingCampaign();
  const updateCampaign = useUpdateCampaign();
  const handlePlanSimulation = async (data: SimulationPlanData) => {
    setIsModalOpen(false);

    if (data.id) {
      try {
        await updateCampaign.update({
          campaignId: data.id,
          updates: {
            launchDate: data.startDate as Date,
            sendByDate: data.endDate as Date,
            ...getTargetsAndExcludes(data.targetGroup ?? [], []),
          },
        });
        await refetchCampaigns();
        await refetchAutoPhishingSummary();
        await refetchSimplifiedScreenCheck();
        toast?.success(t('toast.success'), t('phishing.simulation.updated'));
        setSelectedTask(null);
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    } else {
      try {
        const accountId = (currentAccount as Account)?.id;
        if (!accountId || !data.campaignTemplateId) return;

        await createAutoPhishing.create(accountId, {
          campaignTemplateId: data.campaignTemplateId,
          launchDate: (data.startDate as Date).toISOString(),
          sendByDate: (data.endDate as Date).toISOString(),
          ...getTargetsAndExcludes(data.targetGroup ?? [], []),
        });

        await refetchCampaigns();
        await refetchAutoPhishingSummary();
        await refetchSimplifiedScreenCheck();
        toast?.success(t('toast.success'), t('phishing.simulation.created'));
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTask(null);
  };

  const handleStartWhitelisting = () => {
    setIsWhitelistingModalOpen(true);
  };

  const handleCloseWhitelistingModal = () => {
    setIsWhitelistingModalOpen(false);
  };

  const createAutoPhishingCampaigns = useCreateAutoPhishingCampaigns();

  const {
    lastMessage: lastMessageAiSchedule,
    setNotificationParam: setJobIdForAiSchedule,
    notificationParam: jobIdForAiSchedule,
    isConnected: isConnectedForAiSchedule,
  } = useNotifications(client.campaigns.autoPhishingCampaignsNotifyUrl);

  useEffect(() => {
    if (!jobIdForAiSchedule) return;
    handleNotifyAiScheduleCreate(lastMessageAiSchedule);
  }, [JSON.stringify(lastMessageAiSchedule)]);

  const handleNotifyAiScheduleCreate = async (messages: any) => {
    if (
      !messages?.data?.event ||
      ![
        'auto-phishing-campaigns.create.finished',
        'auto-phishing-campaigns.create.failed',
      ].includes(messages?.data?.event)
    )
      return;

    if (messages?.data?.event === 'auto-phishing-campaigns.create.finished') {
      await refetchCampaigns();
      await refetchAutoPhishingSummary();
      toast?.success(t('toast.success'), t('phishing.simulations.created'));
    }

    if (messages?.data?.event === 'auto-phishing-campaigns.create.failed') {
      toast?.error(
        t('toast.error'),
        messages?.data?.error || t('phishing.simulations.failedToCreate'),
      );
    }

    setIsGeneratingSchedule(false);
    setIsPlanWithAssistanceModalOpen(false);
    setJobIdForAiSchedule(undefined);
  };

  const handleCreateSchedule = async (
    data: SimulationWithAssistancePlanData,
  ) => {
    const accountId = (currentAccount as Account)?.id;
    if (!accountId) return;

    setIsGeneratingSchedule(true);

    try {
      const response = await createAutoPhishingCampaigns.create(accountId, {
        period: data.timePeriod,
        campaignsCount: data.numberOfSimulations,
      });

      const jobId = response?.message;

      if (jobId) {
        setJobIdForAiSchedule(jobId);

        // Wait for a notify connection to establish
        await new Promise((resolve) => {
          const intervalId = setInterval(() => {
            if (isConnectedForAiSchedule.current) {
              clearInterval(intervalId);
              resolve(true);
            }
          }, 100);
        });
      } else {
        setIsGeneratingSchedule(false);
        setIsPlanWithAssistanceModalOpen(false);
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setIsGeneratingSchedule(false);
      setIsPlanWithAssistanceModalOpen(false);
    }
  };
  const handleClosePlanWithAssistanceModal = () => {
    setIsPlanWithAssistanceModalOpen(false);
  };

  const {
    lastMessage: lastMessageSchedule,
    setNotificationParam: setAccountIdForSchedule,
    notificationParam: notificationForSchedule,
    isConnected: isConnectedForSchedule,
  } = useNotifications(client.campaigns.campaingsAccountNotifyUrl);

  const syncedCampaingsRef = useRef<string[]>([]);
  const failedToLaunchCampaignsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!notificationForSchedule) return;
    handleNotifyCampaignSchedule(lastMessageSchedule);
  }, [JSON.stringify(lastMessageSchedule)]);

  const draftCampaigns = campaingsMapped.filter(
    (c) => c.status === CampaignStatusesEnum.DRAFT,
  );

  const handleNotifyCampaignSchedule = async (messages: any) => {
    if (
      !messages?.data?.event ||
      ![
        'campaign.create.sync.finished',
        'campaign.create.sync.failed',
      ].includes(messages?.data?.event)
    )
      return;

    if (messages?.data?.event === 'campaign.create.sync.finished') {
      syncedCampaingsRef.current.push(messages?.data?.payload?.id);
    }

    if (messages?.data?.event === 'campaign.create.sync.failed') {
      syncedCampaingsRef.current.push(messages?.data?.payload?.id);
      failedToLaunchCampaignsRef.current.push(messages?.data?.payload?.id);
    }

    if (syncedCampaingsRef.current.length === draftCampaigns.length) {
      await refetchCampaigns();
      await refetchAutoPhishingSummary();

      if (failedToLaunchCampaignsRef.current.length > 0) {
        toast?.error(
          t('toast.error'),
          t('phishing.simulations.failedToLaunch', {
            count: failedToLaunchCampaignsRef.current.length,
          }),
        );
      } else {
        toast?.success(t('toast.success'), t('phishing.simulations.scheduled'));
      }

      setIsLaunchingSimulations(false);

      setAccountIdForSchedule(undefined);
      syncedCampaingsRef.current = [];
      failedToLaunchCampaignsRef.current = [];
    }
  };

  const launchAutoPhishing = useLaunchAutoPhishingCampaigns();
  const launchSimulations = async () => {
    const accountId = (currentAccount as Account)?.id;
    if (!accountId) return;

    const draftCampaignIds = (campaingsMapped || [])
      .filter((c) => c.status === CampaignStatusesEnum.DRAFT)
      .map((c) => c.id);

    setIsLaunchingSimulations(true);
    setAccountIdForSchedule(accountId);

    // Wait for a notify connection to establish
    await new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (isConnectedForSchedule.current) {
          clearInterval(intervalId);
          resolve(true);
        }
      }, 100);
    });

    try {
      if (isConnectedForSchedule.current) {
        await launchAutoPhishing.launch(accountId, draftCampaignIds);
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setIsLaunchingSimulations(false);
    }
  };

  const handleScheduleSimulations = async () => {
    const accountId = (currentAccount as Account)?.id;
    if (!accountId) return;

    try {
      const usersResponse = await client.users.getAccountUsers(
        {
          take: 0,
          skip: 0,
          filters: [equal('active', true)],
        },
        accountId,
      );

      setActiveUsersCount(usersResponse.count);

      if (
        !autoPhishingSummary?.scheduledCount &&
        !autoPhishingSummary?.activeCount
      ) {
        setIsUsersWarningModalOpen(true);
        return;
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      return;
    }

    launchSimulations();
  };

  const hasDateMissingDraftCampaigns = useMemo(() => {
    return draftCampaigns.some((campaign) => !campaign.startDate);
  }, [draftCampaigns]);

  const hasSimplifiedPhishingPlanningScreenFlag = canUseFeature(
    PostHogFeatureFlags.SIMPLIFIED_PHISHING_PLANNING_SCREEN,
  );

  if (
    initialLoad &&
    (isLoading ||
      isLoadingAutoPhishingSummary ||
      isLoadingSimplifiedScreenCheck)
  ) {
    return (
      <FlexContainer direction="column" className="mt-5" height="100%">
        <ProgressSpinner />
        <h3>{t('phishing.simulations.loading')}</h3>
      </FlexContainer>
    );
  }

  return (
    <FlexContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      style={{ minHeight: '100%' }}
    >
      {!simplifiedScreenCheck?.count &&
      hasSimplifiedPhishingPlanningScreenFlag &&
      [SystemRoles.ADMIN, SystemRoles.MULTIPLE_ACCOUNTS_ADMIN].includes(
        currentUser?.role.code as SystemRoles,
      ) ? (
        <EmptyAutoPhishingPage
          handlePlanSimulation={handlePlanSimulation}
          selectedTask={selectedTask}
          setSelectedTask={setSelectedTask}
        />
      ) : (
        <>
          <AppBreadCrumb model={pathItems(t, currentAccount as Account)} />
          <h1 className="mb-0">{t('phishing.simulations.title')}</h1>
          <StyledDescription className="mb-4">
            {t('phishing.simulations.description')}
          </StyledDescription>
          <DemoContainer>
            {!isPhishingSenderWhitelisted && (
              <WhitelistingBanner
                id="start-whitelisting"
                onStartWhitelisting={
                  isDemoAccount ? undefined : handleStartWhitelisting
                }
                className="mb-4"
              />
            )}

            <InfoCards autoPhishingSummary={autoPhishingSummary} />

            <SchedulerWrapper>
              <AutoScheduleHeader
                currentAccount={currentAccount as Account}
                isDemoAccount={isDemoAccount}
              />
              <StyledScheduler>
                <SimulationScheduler
                  className="simulation-scheduler"
                  simulations={campaingsMapped ?? []}
                  deletingSimulationId={deletingCampaignId}
                  onSimulationChange={
                    isDemoAccount ? undefined : handleCampaignChange
                  }
                  onSimulationDelete={
                    isDemoAccount ? undefined : handleCampaignDelete
                  }
                  onSimulationEdit={
                    isDemoAccount ? undefined : handleCampaignEdit
                  }
                  onSimulationSendTestEmail={
                    isDemoAccount
                      ? undefined
                      : (sim) => setTestEmailSimulation(sim)
                  }
                />
              </StyledScheduler>
            </SchedulerWrapper>

            <AddSimulationsCard
              disabledPlanWithAssistance={!!campaingsMapped?.length}
              onSingleSimulation={handleSingleSimulation}
              onPlanWithAssistance={handlePlanWithAssistance}
            />

            <Actions>
              <div id="schedule-simulations" style={{ flexShrink: 0 }}>
                <AppButton
                  isDisabled={
                    !isPhishingSenderWhitelisted ||
                    hasDateMissingDraftCampaigns ||
                    campaingsMapped.every(
                      (campaign) =>
                        campaign.status !== CampaignStatusesEnum.DRAFT,
                    )
                  }
                  iconPos="right"
                  onClick={
                    isDemoAccount ? undefined : handleScheduleSimulations
                  }
                >
                  <span className="mr-2">
                    {t('phishing.simulations.scheduleButton')}
                  </span>
                </AppButton>
              </div>
              {campaingsMapped.some(
                (campaign) => campaign.status === CampaignStatusesEnum.DRAFT,
              ) && (
                <FlexContainer className="info-message" justify="flex-start">
                  <i className="pi pi-info-circle"></i>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'phishing.simulations.unscheduledWarning.message',
                        {
                          count: campaingsMapped?.filter(
                            (campaign) =>
                              campaign.status === CampaignStatusesEnum.DRAFT,
                          ).length,
                        },
                      ),
                    }}
                  />
                </FlexContainer>
              )}
              {!isPhishingSenderWhitelisted && (
                <Tooltip
                  target="#schedule-simulations"
                  content={t('phishing.simulations.scheduleButtonTooltip')}
                  position="top"
                />
              )}
            </Actions>
          </DemoContainer>

          {(isGeneratingSchedule || isLaunchingSimulations) && (
            <LoadingOverlay className="loading-overlay">
              <div className="loading-content">
                <ProgressSpinner />
                <div>
                  <p className="loading-text">
                    {isGeneratingSchedule
                      ? t('phishing.simulations.loading.generating')
                      : t('phishing.simulations.loading.scheduling')}
                    <span className="loading-dots">
                      <span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </span>
                  </p>
                  {isGeneratingSchedule && (
                    <p className="loading-subtext">
                      {t('phishing.simulations.loading.takeMinutes')}
                    </p>
                  )}
                </div>
              </div>
            </LoadingOverlay>
          )}
        </>
      )}
      <PlanSimulationModal
        selectedTask={selectedTask}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onPlanSimulation={handlePlanSimulation}
      />
      {currentAccount?.id && (
        <WhitelistingModal
          isOpen={isWhitelistingModalOpen}
          onClose={handleCloseWhitelistingModal}
          accountId={currentAccount.id}
        />
      )}

      <PlanWithAssistanceModal
        isOpen={isPlanWithAssistanceModalOpen}
        onClose={handleClosePlanWithAssistanceModal}
        onCreateSchedule={handleCreateSchedule}
      />
      <ScheduleUsersWarningModal
        isOpen={isUsersWarningModalOpen}
        onClose={() => setIsUsersWarningModalOpen(false)}
        onScheduleAnyway={() => {
          setIsUsersWarningModalOpen(false);
          launchSimulations();
        }}
        activeUsersCount={activeUsersCount}
      />
      <SendTestEmailDialog
        visible={
          !!testEmailSimulation?.emailTemplateId &&
          !!testEmailSimulation?.sendingProfileId &&
          !!testEmailSimulation?.landingPageId
        }
        onHide={() => setTestEmailSimulation(null)}
        campaignId={testEmailSimulation?.id ?? ''}
        emailTemplateId={testEmailSimulation?.emailTemplateId ?? ''}
        sendingProfileId={testEmailSimulation?.sendingProfileId ?? ''}
        landingPageId={testEmailSimulation?.landingPageId ?? ''}
      />
    </FlexContainer>
  );
};
